import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Card, Grid, styled, Typography, useTheme, alpha,
  Skeleton, Stack, Fade, Chip, Avatar, Divider, IconButton,
  Tooltip, Button, TextField, InputAdornment, Menu, MenuItem,
  ListItemIcon, ListItemText, Snackbar, Alert,
} from "@mui/material";
import {
  DashboardCustomizeOutlined, VerifiedUserOutlined,
  CalendarTodayOutlined, EventOutlined, WarningAmberOutlined,
  AccessTimeOutlined, PersonOutlined, OpenInNewOutlined,
  WhatsApp, Phone, SearchOutlined, CakeOutlined,
  FavoriteOutlined, CheckCircleOutline, UpdateOutlined,
  LocationOnOutlined, WorkOutline, TableView, PictureAsPdf,
  FileDownload, KeyboardArrowDown, KeyboardArrowUp,
  NotificationsActiveOutlined, NotificationsOutlined,
  CalendarMonth, ArrowForward, CampaignOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { capitalizeWords } from "functions";

import CelebrationsWidget from "./shared/CelebrationsWidget";
import FollowupDetailsDialog from "./shared/FollowupDetailsDialog";
import CelebrationDetailsDialog from "./shared/CelebrationDetailsDialog";
import ProspectDetailsDialog from "./shared/ProspectDetailsDialog";
import { VIEW_PROSPECT, VIEW_REFERENCE } from "Permissions";
import { maskMobileNumber } from "./shared/dashboardMaskUtils";
import {
  exportFollowupsToExcel,
  exportFollowupsToPDF,
  exportCelebrationsToExcel,
  exportCelebrationsToPDF,
} from "./shared/dashboardExportUtils";

import useAuth from "app/hooks/useAuth";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(160deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${theme.palette.background.default} 60%)`
      : `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 60%)`,
  padding: "28px 32px",
  [theme.breakpoints.down("sm")]: { padding: "16px" },
}));

const WelcomeCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  marginBottom: theme.spacing(3),
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.secondary.dark, 0.2)} 100%)`
      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  borderRadius: 20,
  boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
  border: "none",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""', position: "absolute", top: -50, right: -50,
    width: 220, height: 220, borderRadius: "50%", background: alpha("#fff", 0.06), pointerEvents: "none",
  },
  "&::after": {
    content: '""', position: "absolute", bottom: -60, right: 100,
    width: 150, height: 150, borderRadius: "50%", background: alpha("#fff", 0.04), pointerEvents: "none",
  },
}));

const GlassCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: theme.palette.mode === "dark"
    ? alpha(theme.palette.background.paper, 0.65)
    : alpha(theme.palette.background.paper, 0.94),
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(
    theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main, 0.08
  )}`,
  borderRadius: 18,
  boxShadow: theme.palette.mode === "dark"
    ? `0 8px 32px ${alpha(theme.palette.common.black, 0.35)}`
    : `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.25s ease, box-shadow 0.25s ease",
  "&:hover": {
    boxShadow: theme.palette.mode === "dark"
      ? `0 12px 40px ${alpha(theme.palette.common.black, 0.45)}`
      : `0 8px 28px ${alpha(theme.palette.primary.main, 0.11)}`,
  },
}));

const MetricPill = styled(Box)(({ theme, active }) => ({
  padding: "10px 16px",
  borderRadius: 14,
  background: alpha("#fff", active ? 0.22 : 0.12),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha("#fff", active ? 0.35 : 0.18)}`,
  display: "flex",
  alignItems: "center",
  gap: 12,
  color: "#fff",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha("#fff", 0.25),
    transform: "translateY(-2px)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  fontWeight: 800,
  marginBottom: 12,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const PulsingDot = styled(Box)(({ theme }) => ({
  width: 9, height: 9, borderRadius: "50%",
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.25)}`,
  animation: "pulse 2s infinite",
  "@keyframes pulse": {
    "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.45)}` },
    "70%": { boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0)}` },
    "100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}` },
  },
}));

const FollowupCardItem = styled(Box)(({ theme, category }) => {
  const borderColors = {
    today: theme.palette.warning.main,
    tomorrow: theme.palette.primary.main,
    overdue: theme.palette.error.main,
    future: theme.palette.info.main,
  };
  const color = borderColors[category] || theme.palette.primary.main;
  return {
    padding: theme.spacing(1.75, 2),
    borderRadius: 14,
    background:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.4)
        : alpha("#fff", 0.7),
    border: `1px solid ${alpha(color, 0.2)}`,
    borderLeftWidth: 4,
    borderLeftColor: color,
    transition: "all 0.2s ease",
    "&:hover": {
      background: alpha(color, 0.05),
      transform: "translateX(2px)",
      boxShadow: `0 4px 16px ${alpha(color, 0.12)}`,
    },
  };
});

const ProspectCardItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75, 2),
  borderRadius: 14,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.4)
      : alpha("#fff", 0.7),
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
  },
}));

const GlassNotificationBanner = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha("#1E293B", 0.92)} 0%, ${alpha("#0F172A", 0.96)} 100%)`
      : `linear-gradient(135deg, ${alpha("#FFFFFF", 0.98)} 0%, ${alpha("#F8FAFC", 0.95)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(
    theme.palette.mode === "dark" ? "#475569" : theme.palette.primary.main,
    0.16
  )}`,
  borderRadius: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 36px rgba(0,0,0,0.45)"
      : "0 8px 30px rgba(37,99,235,0.08)",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}));

// ─── DATE HELPERS & NORMALIZATION ────────────────────────────────────────────

function parseAppointmentDate(item) {
  const dateStr = item?.NextAppointment || item?.NextAppointmentDate;
  if (!dateStr || dateStr.startsWith("00/00/0000") || dateStr.startsWith("0000-00-00")) {
    return null;
  }
  const parts = dateStr.split(" ");
  const dateParts = parts[0]?.split("/");
  if (dateParts?.length === 3) {
    const day = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1;
    const year = parseInt(dateParts[2], 10);
    let hours = 0;
    let minutes = 0;
    if (parts[1]) {
      const timeParts = parts[1].split(":");
      hours = parseInt(timeParts[0], 10) || 0;
      minutes = parseInt(timeParts[1], 10) || 0;
      if (parts[2]?.toUpperCase() === "PM" && hours < 12) hours += 12;
      if (parts[2]?.toUpperCase() === "AM" && hours === 12) hours = 0;
    }
    const d = new Date(year, month, day, hours, minutes);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

function getFollowupCategory(item) {
  // 1. Direct match from backend AppointmentStatus ('Today', 'Tomorrow', 'Past', 'Future')
  if (item?.AppointmentStatus) {
    const status = item.AppointmentStatus.toLowerCase();
    if (status === "today") return "today";
    if (status === "tomorrow") return "tomorrow";
    if (status === "past") return "overdue";
    if (status === "future") return "future";
  }

  // 2. Fallback to parsing appointment date
  const d = parseAppointmentDate(item);
  if (!d) return "other";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const itemDate = new Date(d);
  itemDate.setHours(0, 0, 0, 0);

  if (itemDate.getTime() < today.getTime()) return "overdue";
  if (itemDate.getTime() === today.getTime()) return "today";
  if (itemDate.getTime() === tomorrow.getTime()) return "tomorrow";
  return "future";
}

function formatAppointmentTime(dateStr) {
  if (!dateStr || dateStr.startsWith("00/00/0000") || dateStr.startsWith("0000-00-00")) {
    return "Time not set";
  }
  const parts = dateStr.split(" ");
  if (parts.length >= 3) {
    return `${parts[1]} ${parts[2]}`;
  }
  return dateStr;
}

function formatAppointmentFull(dateStr) {
  if (!dateStr || dateStr.startsWith("00/00/0000") || dateStr.startsWith("0000-00-00")) {
    return "Date not scheduled";
  }
  return dateStr;
}

function normalizeFollowupItem(raw) {
  const nextAppt = raw.NextAppointment || raw.NextAppointmentDate || "";
  const phone = raw.MobileNo || raw.WhatsAppNo || raw.Mobile || "";
  const wa = raw.WhatsAppNo || raw.MobileNo || raw.Mobile || "";
  const idOrRef = raw.Reference || raw.ID || (raw.SrNo ? `REFERENCE/${raw.SrNo}` : "");
  const place = raw.NextPlace || raw.Place || "";
  const notes = raw.Discussed || raw.Discussion || raw.Subject || "";
  const category = getFollowupCategory(raw);

  return {
    ...raw,
    NextAppointment: nextAppt,
    NextAppointmentDate: nextAppt,
    Mobile: phone,
    MobileNo: phone,
    WhatsAppNo: wa,
    ID: idOrRef,
    Reference: idOrRef,
    Place: place,
    NextPlace: place,
    Discussed: notes,
    _category: category,
  };
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function Analytics() {
  const { user, permissions = [] } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const theme = useTheme();

  // Permission: only show recent prospects if SuperAdmin or has VIEW_PROSPECT permission
  const canViewProspect = Boolean(
    user?.Type === "SuperAdmin" ||
    (Array.isArray(permissions) && (permissions.includes(VIEW_PROSPECT) || permissions.includes(VIEW_REFERENCE)))
  );

  const [loading, setLoading] = useState(true);

  // Core 3 Datasets
  const [pendingFollowups, setPendingFollowups] = useState([]);
  const [recentProspects, setRecentProspects] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);

  // Filters & State
  const [followupTab, setFollowupTab] = useState("today"); // "today" | "tomorrow" | "overdue" | "both"
  const [followupSearch, setFollowupSearch] = useState("");
  const [prospectSearch, setProspectSearch] = useState("");

  // Modal Dialogs
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [selectedCelebration, setSelectedCelebration] = useState(null);

  // Snackbar for permission / alert notifications
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const enqueueSnackbar = useCallback((message, options = {}) => {
    setSnackbarState({
      open: true,
      message: typeof message === "string" ? message : "Notification",
      severity: options.variant || "error",
    });
  }, []);

  const handleCloseSnackbar = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  // Top Notification Bar State
  const [notificationBarExpanded, setNotificationBarExpanded] = useState(true);
  const [notificationCategoryFilter, setNotificationCategoryFilter] = useState("all"); // "all" | "followup" | "birthday" | "anniversary"

  // Celebrations Export Dropdown Menu
  const [celebrationExportAnchor, setCelebrationExportAnchor] = useState(null);
  const isCelebrationExportOpen = Boolean(celebrationExportAnchor);

  // Today formatted string for hero header
  const todayFormatted = useMemo(() => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  // Fetch Dashboard Data
  useEffect(() => {
    let mounted = true;
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const followupEndpoint = "LoadAllPendingFollowUps.php";

        const [dashRes, pendRes, bdayRes, annivRes] = await Promise.all([
          axiosPrivate.post("Dashboard.php"),
          axiosPrivate.post(followupEndpoint),
          axiosPrivate.post("LoadReferenceBirthday.php"),
          axiosPrivate.post("LoadReferenceAnniversary.php"),
        ]);

        if (!mounted) return;

        // 1. Recent prospects worked/updated (only if permitted)
        if (canViewProspect && dashRes.data?.status === 1 && Array.isArray(dashRes.data?.Prospect)) {
          setRecentProspects(dashRes.data.Prospect);
        }

        // 2. Pending followups
        if (pendRes.data?.status === 1 && Array.isArray(pendRes.data?.Data)) {
          setPendingFollowups(pendRes.data.Data);
        }

        // 3. Celebrations
        if (bdayRes.data?.status === 1 && Array.isArray(bdayRes.data?.Data)) {
          setBirthdays(bdayRes.data.Data);
        }
        if (annivRes.data?.status === 1 && Array.isArray(annivRes.data?.Data)) {
          setAnniversaries(annivRes.data.Data);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDashboardData();
    return () => {
      mounted = false;
    };
  }, [axiosPrivate, canViewProspect]);

  // Normalize and categorize followups
  const categorizedFollowups = useMemo(() => {
    return pendingFollowups.map(normalizeFollowupItem);
  }, [pendingFollowups]);

  // Today followups: sorted chronologically (earliest time first)
  const todayFollowups = useMemo(() => {
    return categorizedFollowups
      .filter((f) => f._category === "today")
      .sort((a, b) => {
        const tA = parseAppointmentDate(a)?.getTime() || 0;
        const tB = parseAppointmentDate(b)?.getTime() || 0;
        return tA - tB;
      });
  }, [categorizedFollowups]);

  // Tomorrow followups: sorted chronologically
  const tomorrowFollowups = useMemo(() => {
    return categorizedFollowups
      .filter((f) => f._category === "tomorrow")
      .sort((a, b) => {
        const tA = parseAppointmentDate(a)?.getTime() || 0;
        const tB = parseAppointmentDate(b)?.getTime() || 0;
        return tA - tB;
      });
  }, [categorizedFollowups]);

  // Overdue followups: sorted most recent past first
  const overdueFollowups = useMemo(() => {
    return categorizedFollowups
      .filter((f) => f._category === "overdue")
      .sort((a, b) => {
        const tA = parseAppointmentDate(a)?.getTime() || 0;
        const tB = parseAppointmentDate(b)?.getTime() || 0;
        return tB - tA;
      });
  }, [categorizedFollowups]);

  // Auto-select tomorrow if today has 0 followups and tomorrow has some
  useEffect(() => {
    if (followupTab === "today" && todayFollowups.length === 0 && tomorrowFollowups.length > 0) {
      setFollowupTab("tomorrow");
    }
  }, [todayFollowups.length, tomorrowFollowups.length, followupTab]);

  // Celebrations counts (Today & Tomorrow)
  const celebrationsTodayTomorrowCount = useMemo(() => {
    const b = birthdays.filter((c) => c?.DateCategory === "Today" || c?.DateCategory === "Tomorrow").length;
    const a = anniversaries.filter((c) => c?.DateCategory === "Today" || c?.DateCategory === "Tomorrow").length;
    return b + a;
  }, [birthdays, anniversaries]);

  // Exact Today's Celebrations (Birthdays & Anniversaries)
  const todayBirthdays = useMemo(() => {
    return birthdays.filter((c) => c?.DateCategory === "Today");
  }, [birthdays]);

  const todayAnniversaries = useMemo(() => {
    return anniversaries.filter((c) => c?.DateCategory === "Today");
  }, [anniversaries]);

  const totalTodayNotificationsCount = useMemo(() => {
    return todayFollowups.length + todayBirthdays.length + todayAnniversaries.length;
  }, [todayFollowups.length, todayBirthdays.length, todayAnniversaries.length]);

  // Combined notification items for top notification bar
  const todayNotificationItems = useMemo(() => {
    const items = [];

    // Followups
    if (notificationCategoryFilter === "all" || notificationCategoryFilter === "followup") {
      todayFollowups.forEach((f) => {
        items.push({
          id: `followup-${f.UniqueID || f.ID}`,
          type: "followup",
          title: capitalizeWords(f.Name || "Prospect"),
          subtitle: f.NextPlace || f.Village || f.FamilyMember || "Scheduled Followup",
          timeStr: formatAppointmentTime(f.NextAppointment),
          notes: f.Discussed || f.Discussion || f.Subject || "Followup appointment",
          mobile: f.Mobile || f.MobileNo || f.WhatsAppNo,
          raw: f,
        });
      });
    }

    // Birthdays
    if (notificationCategoryFilter === "all" || notificationCategoryFilter === "birthday") {
      todayBirthdays.forEach((b) => {
        const name = capitalizeWords(b.FamilyMember || b.Name || "Celebrant");
        items.push({
          id: `bday-${b.ID || b.MainMemberID || Math.random()}`,
          type: "birthday",
          title: name,
          subtitle: `Birthday Today! ${b.YearsCompleted != null ? `Turns ${b.YearsCompleted} Yrs` : ""}`,
          location: [b.Village, b.Tehsil, b.District].filter(Boolean).join(", "),
          mobile: b.Mobile || b.WhatsAppNo,
          raw: b,
        });
      });
    }

    // Anniversaries
    if (notificationCategoryFilter === "all" || notificationCategoryFilter === "anniversary") {
      todayAnniversaries.forEach((a) => {
        const name = capitalizeWords(a.FamilyMember || a.Name || "Couple");
        items.push({
          id: `anniv-${a.ID || a.MainMemberID || Math.random()}`,
          type: "anniversary",
          title: name,
          subtitle: `Wedding Anniversary! ${a.YearsCompleted != null ? `${a.YearsCompleted}th Yr` : ""}`,
          location: [a.Village, a.Tehsil, a.District].filter(Boolean).join(", "),
          mobile: a.Mobile || a.WhatsAppNo,
          raw: a,
        });
      });
    }

    return items;
  }, [notificationCategoryFilter, todayFollowups, todayBirthdays, todayAnniversaries]);

  // Filtered followups list based on active tab and search
  const displayedFollowups = useMemo(() => {
    let list = [];
    if (followupTab === "today") list = todayFollowups;
    else if (followupTab === "tomorrow") list = tomorrowFollowups;
    else if (followupTab === "overdue") list = overdueFollowups;
    else if (followupTab === "both") list = [...todayFollowups, ...tomorrowFollowups];
    else list = categorizedFollowups;

    if (followupSearch.trim()) {
      const q = followupSearch.toLowerCase().trim();
      list = list.filter(
        (f) =>
          f.Name?.toLowerCase().includes(q) ||
          f.Reference?.toLowerCase().includes(q) ||
          f.ID?.toLowerCase().includes(q) ||
          (canViewProspect && f.MobileNo?.includes(q)) ||
          (canViewProspect && f.WhatsAppNo?.includes(q)) ||
          f.Village?.toLowerCase().includes(q) ||
          f.Discussed?.toLowerCase().includes(q) ||
          f.FamilyMemberName?.toLowerCase().includes(q) ||
          f.AddedBy?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [
    followupTab,
    todayFollowups,
    tomorrowFollowups,
    overdueFollowups,
    categorizedFollowups,
    followupSearch,
  ]);

  // Filtered recent prospects based on search
  const displayedProspects = useMemo(() => {
    if (!canViewProspect) return [];
    if (!prospectSearch.trim()) return recentProspects;
    const q = prospectSearch.toLowerCase().trim();
    return recentProspects.filter(
      (p) =>
        p.Name?.toLowerCase().includes(q) ||
        p.SrNo?.toString().includes(q) ||
        p.Village?.toLowerCase().includes(q) ||
        p.ModifiedBy?.toLowerCase().includes(q) ||
        p.Status?.toLowerCase().includes(q)
    );
  }, [recentProspects, prospectSearch, canViewProspect]);

  // Action handlers
  const handleCall = useCallback((mobile) => {
    if (!canViewProspect) {
      enqueueSnackbar("You do not have permission to view or call this number", { variant: "error" });
      return;
    }
    if (!mobile) return;
    window.open(`tel:${mobile}`);
  }, [canViewProspect, enqueueSnackbar]);

  const handleWhatsAppFollowup = useCallback((mobile, name, nextDate, topic) => {
    if (!canViewProspect) {
      enqueueSnackbar("You do not have permission to view or contact this number", { variant: "error" });
      return;
    }
    if (!mobile) return;
    const firstName = capitalizeWords(name.split(" ")[0]);
    const dateText = nextDate && !nextDate.startsWith("00/00/0000") ? `scheduled on ${nextDate}` : "scheduled for today";
    const msg = `Hello ${firstName}, gentle reminder regarding our follow-up ${dateText}${topic ? ` to discuss "${topic}"` : ""
      }. Looking forward to connecting!`;
    window.open(`https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`, "_blank");
  }, [canViewProspect, enqueueSnackbar]);

  return (
    <PageWrapper>
      {/* ── Today's Priority Agenda & Notification Bar on Top ── */}
      <Fade in timeout={250}>
        <Box sx={{ mb: 2.5 }}>
          <GlassNotificationBanner>
            {/* Top Bar / Summary Ticker */}
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                background: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.background.paper, 0.45)
                    : alpha("#fff", 0.65),
                borderBottom: notificationBarExpanded
                  ? (t) => `1px solid ${alpha(t.palette.divider, 0.1)}`
                  : "none",
              }}
            >
              {/* Left: Pulsing Bell & Title */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.dark,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 10px ${alpha(theme.palette.warning.main, 0.2)}`,
                    flexShrink: 0,
                  }}
                >
                  <NotificationsActiveOutlined sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.2px", fontSize: "0.95rem" }}
                    >
                      Today's Priority Agenda & Notifications
                    </Typography>
                    {totalTodayNotificationsCount > 0 ? (
                      <Chip
                        label={`${totalTodayNotificationsCount} DUE TODAY`}
                        size="small"
                        color="error"
                        sx={{
                          height: 19,
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          "& .MuiChip-label": { px: 0.8 },
                        }}
                      />
                    ) : (
                      <Chip
                        label="ALL CLEAR"
                        size="small"
                        color="success"
                        sx={{
                          height: 19,
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          "& .MuiChip-label": { px: 0.8 },
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {todayFormatted} • Direct reminders for client appointments, birthdays, and anniversaries
                  </Typography>
                </Box>
              </Box>

              {/* Center / Category Filter Pills */}
              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                <Chip
                  clickable
                  size="small"
                  onClick={() => setNotificationCategoryFilter("all")}
                  label={`All (${totalTodayNotificationsCount})`}
                  variant={notificationCategoryFilter === "all" ? "filled" : "outlined"}
                  color={notificationCategoryFilter === "all" ? "primary" : "default"}
                  sx={{ fontWeight: 700, fontSize: "0.72rem", borderRadius: 2 }}
                />

                <Chip
                  clickable
                  size="small"
                  onClick={() => setNotificationCategoryFilter("followup")}
                  label={`Followups (${todayFollowups.length})`}
                  variant={notificationCategoryFilter === "followup" ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.warning.main, 0.4),
                    color: theme.palette.warning.dark,
                    bgcolor:
                      notificationCategoryFilter === "followup"
                        ? alpha(theme.palette.warning.main, 0.15)
                        : "transparent",
                  }}
                />

                <Chip
                  clickable
                  size="small"
                  onClick={() => setNotificationCategoryFilter("birthday")}
                  label={`Birthdays (${todayBirthdays.length})`}
                  variant={notificationCategoryFilter === "birthday" ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    borderRadius: 2,
                    borderColor: alpha("#F59E0B", 0.4),
                    color: "#D97706",
                    bgcolor:
                      notificationCategoryFilter === "birthday"
                        ? alpha("#F59E0B", 0.15)
                        : "transparent",
                  }}
                />

                <Chip
                  clickable
                  size="small"
                  onClick={() => setNotificationCategoryFilter("anniversary")}
                  label={`Anniversaries (${todayAnniversaries.length})`}
                  variant={notificationCategoryFilter === "anniversary" ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    borderRadius: 2,
                    borderColor: alpha("#EC4899", 0.4),
                    color: "#BE185D",
                    bgcolor:
                      notificationCategoryFilter === "anniversary"
                        ? alpha("#EC4899", 0.15)
                        : "transparent",
                  }}
                />
              </Stack>

              {/* Right: Actions (Calendar Shortcut + Expand/Collapse) */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  component={Link}
                  to="/Calendar"
                  size="small"
                  variant="outlined"
                  startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    height: 30,
                    borderColor: (t) => alpha(t.palette.divider, 0.25),
                    color: "text.primary",
                    "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
                  }}
                >
                  Schedule
                </Button>

                <Tooltip title={notificationBarExpanded ? "Collapse notification bar" : "Expand notification bar"}>
                  <IconButton
                    size="small"
                    onClick={() => setNotificationBarExpanded(!notificationBarExpanded)}
                    sx={{
                      borderRadius: 2,
                      bgcolor: (t) => alpha(t.palette.action.hover, 0.5),
                      p: 0.65,
                    }}
                  >
                    {notificationBarExpanded ? (
                      <KeyboardArrowUp sx={{ fontSize: 19 }} />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 19 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Expanded Cards View */}
            {notificationBarExpanded && (
              <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
                {totalTodayNotificationsCount === 0 ? (
                  <Box
                    sx={{
                      py: 2,
                      px: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      bgcolor: (t) => alpha(t.palette.success.main, 0.06),
                      borderRadius: 3,
                      border: (t) => `1px dashed ${alpha(t.palette.success.main, 0.3)}`,
                    }}
                  >
                    <CheckCircleOutline sx={{ color: "success.main", fontSize: 26 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                        All Caught Up for Today!
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        No pending followups or celebrations scheduled for today. Check tomorrow's followups or browse your calendar schedule.
                      </Typography>
                    </Box>
                    {tomorrowFollowups.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setFollowupTab("tomorrow")}
                        sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.75rem" }}
                      >
                        Check Tomorrow ({tomorrowFollowups.length}) →
                      </Button>
                    )}
                  </Box>
                ) : todayNotificationItems.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ py: 1, display: "block" }}>
                    No items found under the selected category for today.
                  </Typography>
                ) : (
                  <Grid container spacing={1.5}>
                    {todayNotificationItems.map((item) => {
                      const isFollowup = item.type === "followup";
                      const isBday = item.type === "birthday";
                      const isAnniv = item.type === "anniversary";

                      const accentColor = isFollowup
                        ? theme.palette.warning.main
                        : isBday
                          ? "#F59E0B"
                          : "#EC4899";

                      return (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                          <Box
                            onClick={() => {
                              if (!canViewProspect) {
                                enqueueSnackbar("You do not have permission to view this", {
                                  variant: "error",
                                });
                                return;
                              }
                              if (isFollowup) setSelectedFollowup(item.raw);
                              else setSelectedCelebration({ data: item.raw, type: isBday ? "Birthday" : "Anniversary" });
                            }}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                    ? alpha(t.palette.background.paper, 0.7)
                                  : "#ffffff",
                              border: `1px solid ${alpha(accentColor, 0.25)}`,
                              borderLeft: `4px solid ${accentColor}`,
                              cursor: canViewProspect ? "pointer" : "default",
                              transition: "all 0.2s ease",
                              boxShadow: `0 2px 8px ${alpha(accentColor, 0.08)}`,
                              "&:hover": {
                                transform: canViewProspect ? "translateY(-2px)" : "none",
                                boxShadow: canViewProspect ? `0 6px 18px ${alpha(accentColor, 0.16)}` : `0 2px 8px ${alpha(accentColor, 0.08)}`,
                                borderColor: accentColor,
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                              {/* Left: Category Tag + Title */}
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
                                  <Chip
                                    size="small"
                                    label={
                                      isFollowup
                                        ? `📋 ${item.timeStr || "Today"}`
                                        : isBday
                                          ? "🎂 Birthday"
                                          : "💑 Anniversary"
                                    }
                                    sx={{
                                      height: 18,
                                      fontSize: "0.62rem",
                                      fontWeight: 800,
                                      bgcolor: alpha(accentColor, 0.14),
                                      color: accentColor,
                                      border: `1px solid ${alpha(accentColor, 0.3)}`,
                                    }}
                                  />
                                  {isFollowup && item.raw?.AppointmentStatus && (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", fontWeight: 700 }}>
                                      {item.raw.AppointmentStatus}
                                    </Typography>
                                  )}
                                </Box>

                                <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ color: "text.primary" }}>
                                  {item.title}
                                </Typography>

                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                                  {item.subtitle}
                                </Typography>

                                {item.mobile && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.68rem", mt: 0.2 }}>
                                    📞 {canViewProspect ? item.mobile : maskMobileNumber(item.mobile)}
                                  </Typography>
                                )}

                                {item.location && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.3 }}>
                                    <LocationOnOutlined sx={{ fontSize: 13, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.68rem" }}>
                                      {item.location}
                                    </Typography>
                                  </Box>
                                )}

                                {isFollowup && item.notes && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      fontSize: "0.68rem",
                                      mt: 0.4,
                                    }}
                                  >
                                    Notes: {item.notes}
                                  </Typography>
                                )}
                              </Box>

                              {/* Right: Quick Action Buttons */}
                              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                {item.mobile && (
                                  <Tooltip title={canViewProspect ? `Call ${item.mobile}` : "Mobile number masked (permission required)"}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={!canViewProspect}
                                        onClick={() => {
                                          if (!canViewProspect) {
                                            enqueueSnackbar("You do not have permission to view or call this number", { variant: "error" });
                                            return;
                                          }
                                          window.open(`tel:${item.mobile}`);
                                        }}
                                        sx={{
                                          color: "primary.main",
                                          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                          p: 0.6,
                                          "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.18) },
                                        }}
                                      >
                                        <Phone sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}

                                {item.mobile && (
                                  <Tooltip title={canViewProspect ? (isFollowup ? "Send WhatsApp Reminder" : "Send Greetings") : "Mobile number masked (permission required)"}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={!canViewProspect}
                                        onClick={() => {
                                          if (!canViewProspect) {
                                            enqueueSnackbar("You do not have permission to view or contact this number", { variant: "error" });
                                            return;
                                          }
                                          if (isFollowup) {
                                            handleWhatsAppFollowup(item.mobile, item.title, item.raw?.NextAppointment, item.raw?.Discussed);
                                          } else {
                                            const wish = isBday
                                              ? `Happy Birthday ${item.title}! 🎂🎉 Wishing you a wonderful year ahead filled with joy and happiness!`
                                              : `Happy Anniversary ${item.title}! 💑🎉 Wishing you both many more years of love and togetherness!`;
                                            window.open(`https://wa.me/91${item.mobile}?text=${encodeURIComponent(wish)}`, "_blank");
                                          }
                                        }}
                                        sx={{
                                          color: "#25D366",
                                          bgcolor: alpha("#25D366", 0.08),
                                          p: 0.6,
                                          "&:hover": { bgcolor: alpha("#25D366", 0.18) },
                                        }}
                                      >
                                        <WhatsApp sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}
          </GlassNotificationBanner>
        </Box>
      </Fade>

      {/* ── Welcome & Live KPI Bar ── */}
      <Fade in timeout={300}>
        <WelcomeCard elevation={0}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2.5,
            }}
          >
            {/* User Greeting & Date */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: alpha("#fff", 0.14),
                  display: "flex",
                }}
              >
                <DashboardCustomizeOutlined
                  sx={{
                    fontSize: 32,
                    color: theme.palette.mode === "dark" ? theme.palette.primary.light : "#fff",
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    color: theme.palette.mode === "dark" ? "text.primary" : "#fff",
                  }}
                >
                  Welcome back, {user?.Name?.split(" ")[0] || "User"}!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.4,
                    color:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.secondary, 0.85)
                        : alpha("#fff", 0.8),
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <EventOutlined sx={{ fontSize: 16 }} />
                  {todayFormatted}
                </Typography>
              </Box>
            </Box>

            {/* Quick Live Summary Action Badges */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <MetricPill
                active={followupTab === "today"}
                onClick={() => setFollowupTab("today")}
              >
                <CalendarTodayOutlined sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: "0.68rem", textTransform: "uppercase" }}>
                    Today Followups
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                    {loading ? "..." : todayFollowups.length}
                  </Typography>
                </Box>
              </MetricPill>

              <MetricPill
                active={followupTab === "tomorrow"}
                onClick={() => setFollowupTab("tomorrow")}
              >
                <AccessTimeOutlined sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: "0.68rem", textTransform: "uppercase" }}>
                    Tomorrow
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                    {loading ? "..." : tomorrowFollowups.length}
                  </Typography>
                </Box>
              </MetricPill>

              <MetricPill sx={{ cursor: "default" }}>
                <CakeOutlined sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: "0.68rem", textTransform: "uppercase" }}>
                    Celebrations
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                    {loading ? "..." : celebrationsTodayTomorrowCount}
                  </Typography>
                </Box>
              </MetricPill>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0.5 }}>
                <PulsingDot />
                <Chip
                  icon={
                    <VerifiedUserOutlined
                      sx={{
                        fontSize: "14px !important",
                        color: theme.palette.mode === "dark" ? `${theme.palette.primary.light} !important` : "#fff !important",
                      }}
                    />
                  }
                  label={user?.Type || "User"}
                  size="small"
                  sx={{
                    bgcolor: alpha("#fff", 0.15),
                    color: theme.palette.mode === "dark" ? theme.palette.primary.light : "#fff",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    border: `1px solid ${alpha("#fff", 0.2)}`,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </WelcomeCard>
      </Fade>

      {/* ── Main Focused Content ── */}
      <Fade in timeout={500}>
        <Stack spacing={3}>
          {/* ════════════════════════════════════════════════════════════════
             SECTION 1: CELEBRATIONS & GREETINGS (TODAY & TOMORROW ONLY)
             ════════════════════════════════════════════════════════════════ */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.2 }}>
              <SectionTitle sx={{ mb: 0 }}>
                🎉 Celebrations & Greetings (Today & Tomorrow)
              </SectionTitle>

              {/* Quick Export Menu for Celebrations */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setCelebrationExportAnchor(e.currentTarget)}
                  startIcon={<FileDownload sx={{ fontSize: 16 }} />}
                  endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    height: 30,
                    color: "text.primary",
                    borderColor: alpha(theme.palette.divider, 0.25),
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.8) },
                  }}
                >
                  Export Celebrations
                </Button>

                <Menu
                  anchorEl={celebrationExportAnchor}
                  open={isCelebrationExportOpen}
                  onClose={() => setCelebrationExportAnchor(null)}
                  PaperProps={{
                    sx: {
                      borderRadius: 2.5,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      minWidth: 230,
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 0.8, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                      🎂 Birthday Lists
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={() => {
                      setCelebrationExportAnchor(null);
                      exportCelebrationsToExcel(birthdays, "Birthday", "All_Active");
                    }}
                  >
                    <ListItemIcon>
                      <TableView sx={{ fontSize: 18, color: "#16A34A" }} />
                    </ListItemIcon>
                    <ListItemText primary="Birthdays (Excel .xlsx)" primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setCelebrationExportAnchor(null);
                      exportCelebrationsToPDF(birthdays, "Birthday", "All_Active");
                    }}
                  >
                    <ListItemIcon>
                      <PictureAsPdf sx={{ fontSize: 18, color: "#DC2626" }} />
                    </ListItemIcon>
                    <ListItemText primary="Birthdays (PDF Report)" primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }} />
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <Box sx={{ px: 2, py: 0.8, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                      💑 Anniversary Lists
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={() => {
                      setCelebrationExportAnchor(null);
                      exportCelebrationsToExcel(anniversaries, "Anniversary", "All_Active");
                    }}
                  >
                    <ListItemIcon>
                      <TableView sx={{ fontSize: 18, color: "#16A34A" }} />
                    </ListItemIcon>
                    <ListItemText primary="Anniversaries (Excel .xlsx)" primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setCelebrationExportAnchor(null);
                      exportCelebrationsToPDF(anniversaries, "Anniversary", "All_Active");
                    }}
                  >
                    <ListItemIcon>
                      <PictureAsPdf sx={{ fontSize: 18, color: "#DC2626" }} />
                    </ListItemIcon>
                    <ListItemText primary="Anniversaries (PDF Report)" primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }} />
                  </MenuItem>
                </Menu>
              </Stack>
            </Box>
            <CelebrationsWidget
              canViewProspect={canViewProspect}
              onPermissionDenied={() =>
                enqueueSnackbar("You do not have permission to view celebration details", {
                  variant: "error",
                })
              }
            />
          </Box>

          {/* ════════════════════════════════════════════════════════════════
             SECTION 2 & 3: TWO EQUAL / 7:5 POWER COLUMNS
             Column 1: Recent Today & Tomorrow's Followups
             Column 2: Recent Prospects Worked / Updated
             ════════════════════════════════════════════════════════════════ */}
          <Grid container spacing={3}>
            {/* ── LEFT COLUMN: FOLLOWUPS (TODAY & TOMORROW) ── */}
            <Grid item xs={12} lg={canViewProspect ? 7 : 12}>
              <GlassCard>
                {/* Header & Filter Controls */}
                <Box sx={{ pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                    <Box>
                      <SectionTitle sx={{ mb: 0.25 }}>
                        ⏰ Followups Schedule
                      </SectionTitle>
                      <Typography variant="caption" color="text.secondary">
                        Appointments for today and tomorrow
                      </Typography>
                    </Box>

                    {/* Filter Tabs & Export Buttons */}
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                      <Chip
                        label={`Today (${todayFollowups.length})`}
                        size="small"
                        onClick={() => setFollowupTab("today")}
                        color={followupTab === "today" ? "warning" : "default"}
                        variant={followupTab === "today" ? "filled" : "outlined"}
                        sx={{ fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}
                      />
                      <Chip
                        label={`Tomorrow (${tomorrowFollowups.length})`}
                        size="small"
                        onClick={() => setFollowupTab("tomorrow")}
                        color={followupTab === "tomorrow" ? "primary" : "default"}
                        variant={followupTab === "tomorrow" ? "filled" : "outlined"}
                        sx={{ fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}
                      />
                      {overdueFollowups.length > 0 && (
                        <Chip
                          label={`Overdue (${overdueFollowups.length})`}
                          size="small"
                          onClick={() => setFollowupTab("overdue")}
                          color={followupTab === "overdue" ? "error" : "default"}
                          variant={followupTab === "overdue" ? "filled" : "outlined"}
                          sx={{ fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}
                        />
                      )}
                      <Chip
                        label={`Both (${todayFollowups.length + tomorrowFollowups.length})`}
                        size="small"
                        onClick={() => setFollowupTab("both")}
                        color={followupTab === "both" ? "secondary" : "default"}
                        variant={followupTab === "both" ? "filled" : "outlined"}
                        sx={{ fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}
                      />

                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: "center", display: { xs: "none", sm: "block" } }} />

                      {/* Download PDF & Excel buttons */}
                      <Tooltip title={`Download ${followupTab.toUpperCase()} Followups as Excel (.xlsx)`} arrow>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => exportFollowupsToExcel(displayedFollowups, `${followupTab}_Followups`)}
                            disabled={displayedFollowups.length === 0}
                            startIcon={<TableView sx={{ fontSize: "14px !important", color: "#16A34A" }} />}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              borderColor: alpha("#16A34A", 0.3),
                              color: "#16A34A",
                              bgcolor: alpha("#16A34A", 0.05),
                              "&:hover": { bgcolor: alpha("#16A34A", 0.12), borderColor: "#16A34A" },
                              height: 28,
                              px: 1,
                            }}
                          >
                            Excel
                          </Button>
                        </span>
                      </Tooltip>

                      <Tooltip title={`Download ${followupTab.toUpperCase()} Followups as PDF Report`} arrow>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => exportFollowupsToPDF(displayedFollowups, `${followupTab.toUpperCase()} Followups`)}
                            disabled={displayedFollowups.length === 0}
                            startIcon={<PictureAsPdf sx={{ fontSize: "14px !important", color: "#DC2626" }} />}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              borderColor: alpha("#DC2626", 0.3),
                              color: "#DC2626",
                              bgcolor: alpha("#DC2626", 0.05),
                              "&:hover": { bgcolor: alpha("#DC2626", 0.12), borderColor: "#DC2626" },
                              height: 28,
                              px: 1,
                            }}
                          >
                            PDF
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Search bar */}
                  <Box sx={{ mt: 1.5 }}>
                    <TextField
                      size="small"
                      placeholder="Search followups by name, ID, phone, village or notes..."
                      fullWidth
                      value={followupSearch}
                      onChange={(e) => setFollowupSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          fontSize: "0.82rem",
                          bgcolor: alpha(theme.palette.action.hover, 0.3),
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Followups List */}
                {loading ? (
                  <Stack spacing={1.5} sx={{ py: 1 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: 2.5 }} />
                    ))}
                  </Stack>
                ) : displayedFollowups.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                    <CheckCircleOutline sx={{ fontSize: 44, opacity: 0.3, mb: 1, color: theme.palette.success.main }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {followupTab === "today"
                        ? "All caught up for Today!"
                        : followupTab === "tomorrow"
                          ? "No followups scheduled for Tomorrow"
                          : "No followups found for this filter"}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                      {followupTab === "today" && tomorrowFollowups.length > 0 ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setFollowupTab("tomorrow")}
                          sx={{ textTransform: "none", fontWeight: 700, mt: 0.5 }}
                        >
                          Check Tomorrow's Followups ({tomorrowFollowups.length}) →
                        </Button>
                      ) : (
                        "Use the tabs above to view other days or search for prospects."
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.25} sx={{ flex: 1, overflowY: "auto", pr: 0.5, maxHeight: 580 }}>
                    {displayedFollowups.map((item, idx) => {
                      const category = item._category;
                      const isToday = category === "today";
                      const isTomorrow = category === "tomorrow";
                      const isOverdue = category === "overdue";

                      const timeStr = isToday || isTomorrow
                        ? formatAppointmentTime(item.NextAppointment)
                        : formatAppointmentFull(item.NextAppointment);

                      const prospectLink = item.Reference?.startsWith("AGENT")
                        ? `/CLIAProspect/EditCLIAProspect/${item.ProspectID}`
                        : `/Prospects/ViewProspect/${item.ProspectID}`;

                      const hasValidPicture = Boolean(item.Picture && !item.Picture.includes("default") && item.Picture.length > 4);

                      return (
                        <FollowupCardItem
                          key={item.UniqueID || idx}
                          category={category}
                          onClick={() => {
                            if (!canViewProspect) {
                              enqueueSnackbar("You do not have permission to view prospect/followup details", {
                                variant: "error",
                              });
                              return;
                            }
                            setSelectedFollowup(item);
                          }}
                          sx={{ cursor: canViewProspect ? "pointer" : "default" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
                            {/* Left: Avatar & Prospect Info */}
                            <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Avatar
                                src={
                                  hasValidPicture
                                    ? `https://backend.dahiyainsurance.com/Upload/profile/${item.Picture}`
                                    : undefined
                                }
                                sx={{
                                  width: 40,
                                  height: 40,
                                  fontSize: "0.85rem",
                                  fontWeight: 800,
                                  bgcolor: isToday
                                    ? alpha(theme.palette.warning.main, 0.15)
                                    : isTomorrow
                                      ? alpha(theme.palette.primary.main, 0.15)
                                      : alpha(theme.palette.error.main, 0.15),
                                  color: isToday
                                    ? theme.palette.warning.dark
                                    : isTomorrow
                                      ? theme.palette.primary.main
                                      : theme.palette.error.main,
                                  border: `2px solid ${alpha(
                                    isToday
                                      ? theme.palette.warning.main
                                      : isTomorrow
                                        ? theme.palette.primary.main
                                        : theme.palette.error.main,
                                    0.25
                                  )}`,
                                  flexShrink: 0,
                                  mt: 0.25,
                                }}
                              >
                                {item.Name?.[0]?.toUpperCase()}
                              </Avatar>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.4 }}>
                                  <Chip
                                    label={
                                      isToday ? "TODAY" : isTomorrow ? "TOMORROW" : isOverdue ? "OVERDUE" : "UPCOMING"
                                    }
                                    size="small"
                                    color={isToday ? "warning" : isTomorrow ? "primary" : isOverdue ? "error" : "default"}
                                    sx={{ height: 19, fontSize: "0.62rem", fontWeight: 800, "& .MuiChip-label": { px: 0.75 } }}
                                  />

                                  {timeStr && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                      <AccessTimeOutlined sx={{ fontSize: 13, color: "text.secondary" }} />
                                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                                        {timeStr}
                                      </Typography>
                                    </Box>
                                  )}

                                  {item.Reference && (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                                      ({item.Reference})
                                    </Typography>
                                  )}
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                  <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ textTransform: "capitalize", color: "text.primary" }}>
                                    {capitalizeWords(item.Name)}
                                  </Typography>
                                  {item.FamilyMember && item.FamilyMember !== `Self - ${item.Name}` && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                                      ({item.FamilyMember})
                                    </Typography>
                                  )}
                                </Box>

                                {item.Discussed && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      mt: 0.25,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    "{item.Discussed}"
                                  </Typography>
                                )}

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
                                  {(item.Village || item.NextPlace) && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                      <LocationOnOutlined sx={{ fontSize: 12, color: "text.secondary" }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                        {[item.Village, item.NextPlace].filter(Boolean).join(" • ")}
                                      </Typography>
                                    </Box>
                                  )}
                                  {(item.MobileNo || item.WhatsAppNo) && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                      <Phone sx={{ fontSize: 11, color: "text.disabled" }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                                        {canViewProspect
                                          ? (item.MobileNo || item.WhatsAppNo)
                                          : maskMobileNumber(item.MobileNo || item.WhatsAppNo)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {item.AddedBy && (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>
                                      Added by {item.AddedBy}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            {/* Right: Quick Action Buttons */}
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, mt: 0.25 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(item.MobileNo || item.WhatsAppNo) && (
                                <Tooltip
                                  title={
                                    canViewProspect
                                      ? `Call ${item.MobileNo || item.WhatsAppNo}`
                                      : "Mobile number masked (permission required)"
                                  }
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!canViewProspect}
                                      onClick={() => handleCall(item.MobileNo || item.WhatsAppNo)}
                                      sx={{
                                        p: 0.6,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: theme.palette.primary.main,
                                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                                      }}
                                    >
                                      <Phone sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              {(item.WhatsAppNo || item.MobileNo) && (
                                <Tooltip
                                  title={
                                    canViewProspect
                                      ? "Send WhatsApp reminder"
                                      : "Mobile number masked (permission required)"
                                  }
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!canViewProspect}
                                      onClick={() =>
                                        handleWhatsAppFollowup(
                                          item.WhatsAppNo || item.MobileNo,
                                          item.Name,
                                          item.NextAppointment,
                                          item.Discussed
                                        )
                                      }
                                      sx={{
                                        p: 0.6,
                                        bgcolor: alpha("#25D366", 0.1),
                                        color: "#25D366",
                                        "&:hover": { bgcolor: alpha("#25D366", 0.22) },
                                      }}
                                    >
                                      <WhatsApp sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              {/* Quick Link to Prospect Page */}
                              {canViewProspect && (
                                <Tooltip title="View Complete Prospect" arrow>
                                  <IconButton
                                    component={Link}
                                    to={prospectLink}
                                    size="small"
                                    sx={{
                                      p: 0.6,
                                      bgcolor: alpha(theme.palette.text.primary, 0.05),
                                      color: "text.secondary",
                                      "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.12) },
                                    }}
                                  >
                                    <OpenInNewOutlined sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </FollowupCardItem>
                      );
                    })}
                  </Stack>
                )}
              </GlassCard>
            </Grid>

            {/* ── RIGHT COLUMN: RECENT PROSPECTS WORKED / UPDATED (PERMITTED ONLY) ── */}
            {canViewProspect && (
              <Grid item xs={12} lg={5}>
                <GlassCard>
                  {/* Header & Search */}
                  <Box sx={{ pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                      <Box>
                        <SectionTitle sx={{ mb: 0.25 }}>
                          🧑‍💼 Recently Worked Prospects
                        </SectionTitle>
                        <Typography variant="caption" color="text.secondary">
                          Prospects recently modified or updated
                        </Typography>
                      </Box>
                      <Chip
                        label={`${displayedProspects.length} total`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>

                    {/* Search prospects */}
                    <Box sx={{ mt: 1.5 }}>
                      <TextField
                        size="small"
                        placeholder="Search recent prospects..."
                        fullWidth
                        value={prospectSearch}
                        onChange={(e) => setProspectSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: 2,
                            fontSize: "0.82rem",
                            bgcolor: alpha(theme.palette.action.hover, 0.3),
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Prospects List */}
                  {loading ? (
                    <Stack spacing={1.5} sx={{ py: 1 }}>
                      {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 2.5 }} />
                      ))}
                    </Stack>
                  ) : displayedProspects.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                      <PersonOutlined sx={{ fontSize: 44, opacity: 0.25, mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        No recent prospects found
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                        Prospect updates will appear here automatically.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.25} sx={{ flex: 1, overflowY: "auto", pr: 0.5, maxHeight: 580 }}>
                      {displayedProspects.map((item, idx) => (
                        <ProspectCardItem
                          key={item.UniqueID || idx}
                          onClick={() => setSelectedProspect(item)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            {/* Prospect Avatar */}
                            <Avatar
                              src={
                                item.Picture
                                  ? `https://backend.dahiyainsurance.com/Upload/profile/${item.Picture}`
                                  : undefined
                              }
                              sx={{
                                width: 42,
                                height: 42,
                                fontSize: "0.95rem",
                                fontWeight: 800,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                flexShrink: 0,
                              }}
                            >
                              {item.Name?.[0]?.toUpperCase()}
                            </Avatar>

                            {/* Prospect Main Info */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  noWrap
                                  sx={{ textTransform: "capitalize", color: "text.primary" }}
                                >
                                  {capitalizeWords(item.Name)}
                                </Typography>
                                {item.SrNo && (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>
                                    ({item.SrNo})
                                  </Typography>
                                )}
                              </Box>

                              {/* Modified On & Modified By */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                  <UpdateOutlined sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                                    Updated {item.ModifiedOn || "Recently"}
                                  </Typography>
                                </Box>

                                {item.ModifiedBy && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: 0.5 }}>
                                    {item.UserPic ? (
                                      <Avatar
                                        src={`https://backend.dahiyainsurance.com/Upload/profile/${item.UserPic}`}
                                        sx={{ width: 16, height: 16 }}
                                      />
                                    ) : null}
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>
                                      by {item.ModifiedBy}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Location & Status Chips */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
                                {item.Village && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                    <LocationOnOutlined sx={{ fontSize: 11, color: "text.disabled" }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                                      {item.Village}
                                    </Typography>
                                  </Box>
                                )}
                                {item.Status && (
                                  <Chip
                                    label={item.Status}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.58rem",
                                      fontWeight: 700,
                                      bgcolor:
                                        item.Status === "HOT"
                                          ? alpha(theme.palette.error.main, 0.12)
                                          : alpha(theme.palette.info.main, 0.12),
                                      color:
                                        item.Status === "HOT"
                                          ? theme.palette.error.dark
                                          : theme.palette.info.dark,
                                      "& .MuiChip-label": { px: 0.6 },
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* Quick Link Button */}
                            <Box onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="View Complete Prospect" arrow>
                                <IconButton
                                  component={Link}
                                  to={`/Prospects/ViewProspect/${item.UniqueID}`}
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    color: alpha(theme.palette.primary.main, 0.7),
                                    "&:hover": { color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08) },
                                  }}
                                >
                                  <OpenInNewOutlined sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </ProspectCardItem>
                      ))}
                    </Stack>
                  )}
                </GlassCard>
              </Grid>
            )}
          </Grid>
        </Stack>
      </Fade>

      {/* ── Prospect Details Popup Modal (Short / Full Profile) ── */}
      {canViewProspect && (
        <ProspectDetailsDialog
          open={Boolean(selectedProspect)}
          onClose={() => setSelectedProspect(null)}
          prospectId={selectedProspect?.UniqueID || selectedProspect}
          initialData={typeof selectedProspect === "object" ? selectedProspect : null}
        />
      )}

      {/* ── Followup Details Popup Modal ── */}
      {canViewProspect && (
        <FollowupDetailsDialog
          open={Boolean(selectedFollowup)}
          onClose={() => setSelectedFollowup(null)}
          followup={selectedFollowup}
        />
      )}

      {/* ── Celebration Details Popup Modal ── */}
      {canViewProspect && (
        <CelebrationDetailsDialog
          open={Boolean(selectedCelebration)}
          onClose={() => setSelectedCelebration(null)}
          data={selectedCelebration?.data}
          type={selectedCelebration?.type}
        />
      )}

      {/* ── Permission Alert / Notification Snackbar ── */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarState.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2.5, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
}
